import { CapacitorHttp } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';

interface DownloadPhotoData {
  url?: string;
  urlName?: string;
  takenAt?: string;
  isWithMask?: boolean;
}

export const downloadPhotoByUrl = async (dataImg?: DownloadPhotoData): Promise<void> => {
  if (!dataImg) return;

  const imageUrl = dataImg.url;
  if (!imageUrl) return;

  try {
    const response = await fetch(imageUrl, {
      method: 'GET',
    });

    if (response.ok) {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // fileName = '2025-10-26T11-20-AM-qOW3FT9wadM63Dcl.jpg' | 'vegetation-2025-10-26T11-20-AM-qOW3FT9wadM63Dcl.jpg'
      const takenAt = dataImg.takenAt ?? '';
      const imageUrlName = dataImg.urlName || '';

      let fileName = imageUrlName.split('/').pop()?.split('?')[0] || '';
      fileName = fileName.replace(/T[^-]+-/, `T${takenAt.replace(/[: ]/g, '-')}-`);

      link.setAttribute('download', dataImg.isWithMask ? `vegetation-${fileName}` : fileName || 'image.jpg');

      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  } catch (error) {
    console.error('Failed to download image:', error);
  }
};

/**
 * Share an image from a URL on mobile devices using Capacitor's share functionality.
 * @param imageUrl - The URL (remote or base64 data URL) of the image to share.
 * @param title - The title shown in the native share sheet.
 * @param fileName - Optional file name to use for the shared image.
 * @returns A Promise that resolves when the image is shared or rejects if an error occurs.
 */
export const shareImageMobileApp = async (
  imageUrl: string,
  title: string = 'Cropler',
  fileName?: string,
): Promise<void> => {
  try {
    let base64Data: string;
    let resolvedFileName: string;

    if (imageUrl.startsWith('data:')) {
      // Masked image is already a base64 data URL; strip the prefix to get raw base64 data.
      base64Data = imageUrl.split(',')[1] ?? '';
      if (!base64Data) return;
      resolvedFileName = fileName || `image-${Date.now()}.jpg`;
    } else {
      // Fetch the image; on native, responseType 'blob' returns base64 data in response.data.
      const response = await CapacitorHttp.get({
        url: imageUrl,
        responseType: 'blob',
      });
      if (response.status !== 200) return;

      base64Data = response.data;
      // Derive a file name from the URL, with a timestamped fallback.
      resolvedFileName = fileName || imageUrl.split('/').pop()?.split('?')[0] || `image-${Date.now()}.jpg`;
    }

    // Persist the image to the cache directory so we get a local file URI to share.
    const written = await Filesystem.writeFile({
      path: resolvedFileName,
      data: base64Data,
      directory: Directory.Cache,
    });

    // Open the native share sheet with the actual file.
    // On iOS the `title` field is ignored, so we also pass `text` to ensure the caption is shared.
    await Share.share({
      title,
      text: title,
      files: [written.uri],
      dialogTitle: 'Share image',
    });
  } catch (error) {
    console.error('Error during image fetch and share:', error);
  }
};

/**
 * Share an image from a URL on the web application.
 * @param imageUrl - The URL of the image to share.
 * @param title
 * @param fileName
 */
export const shareImageWeb = async (
  imageUrl?: string,
  title: string = 'Cropler',
  fileName?: string,
): Promise<boolean> => {
  if (!imageUrl || !navigator.share) return false;

  try {
    // Download the image and wrap it into a File for sharing.
    const response = await fetch(imageUrl);
    if (!response.ok) return false;

    const blob = await response.blob();
    const resolvedFileName = fileName || imageUrl.split('/').pop()?.split('?')[0] || `image-${Date.now()}.jpg`;
    const file = new File([blob], resolvedFileName, { type: blob.type || 'image/jpeg' });

    // Share the file when the browser supports file sharing, otherwise fall back to the link.
    if (navigator.canShare?.({ files: [file] })) {
      await navigator.share({ title, files: [file] });
    } else {
      await navigator.share({ title, url: imageUrl });
    }
    return true;
  } catch (error) {
    console.error('Error sharing', error);
    return false;
  }
};

const getResizeUrl = (url?: string, width: string = '-', height: string = '-'): string => {
  if (!url) return '';
  const baseUrl = `${import.meta.env.VITE_API_URL}`;
  const cleanedUrl = url.replace(/^.*?(files\/)/, '$1');
  return `${baseUrl}/resize-api/${width}/${height}/${cleanedUrl}`;
};

export const resizeImageCard = (imageUrl: string): string => {
  return getResizeUrl(imageUrl, '760', '-');
};

export const resizeImageTimelinePreview = (imageUrl: string): string => {
  return getResizeUrl(imageUrl, '-', '130');
};
